import { Collapsible, FieldMapper, Flex, FlexProps, Menu, SimpleGrid, Text } from 'components'
import { useAlert } from 'components/providers'
import { isEqual, xor } from 'lodash-es'
import { useEffect, useRef, useState } from 'react'
import { PrimaryLeadProperty } from 'types'

interface Props<T extends string> extends Omit<FlexProps, 'onChange'> {
  _ref?: React.Ref<HTMLDivElement>
  defaultProperties?: T[]
  hideCustom?: boolean
  /** default "File Fields" */
  inputFieldLabel?: string
  valueFieldLabel?: string
  inputMap: Record<T, string>
  onChange: (fieldMap?: Record<T, any>) => void
  /** (default: Object.keys(inputMap)) Available properties to select from */
  properties?: T[]
}

export const defaultPrimaryProperties = [
  'firstName',
  'lastName',
  'email',
  'phone',
] as PrimaryLeadProperty[]

export default function PropertyMapper<T extends string>({
  _ref,
  defaultProperties = [],
  hideCustom,
  inputFieldLabel = 'File Fields',
  valueFieldLabel = 'Scanned Data Input',
  inputMap,
  onChange,
  properties = Object.keys(inputMap) as T[],
  ...rest
}: Props<T>) {
  const [leadMapping, setLeadMapping] = useState({} as Record<string, any>)
  const { alert } = useAlert()

  const mappedProperties = Object.keys(leadMapping) as T[]
  const nonDefaultMappedProperties = mappedProperties.filter(
    key => !defaultProperties.includes(key)
  )
  const mappedInputs = (Object.values(leadMapping) as string[]).filter(Boolean)
  const visibleProperties = [...defaultProperties, ...nonDefaultMappedProperties]
  const repeatedInputs = mappedInputs.sort().filter((val, i) => mappedInputs.includes(val, i + 1))
  const hiddenFields = xor(properties, visibleProperties)
  const customProperties = (Object.keys(inputMap || {}) as T[]).filter(
    key => !Object.values(leadMapping).includes(key)
  )

  useEffect(() => {
    if (repeatedInputs.length) {
      onChange()
    } else {
      const cleaned = Object.entries(leadMapping).reduce((acc, [key, value]) => {
        if (value === '') return acc
        return { ...acc, [key]: value }
      }, {} as Record<string, any>)
      onChange(cleaned)
    }
  }, [leadMapping])

  const cachedInputMap = useRef(inputMap)
  if (!isEqual(cachedInputMap.current, inputMap)) cachedInputMap.current = inputMap
  const cachedProperties = useRef(properties)
  if (!isEqual(cachedProperties.current, properties)) cachedProperties.current = properties

  useEffect(() => {
    const autoMapped = Object.keys(cachedInputMap.current).reduce((acc, inputProperty) => {
      const closestProperty = getClosestProperty(inputProperty, cachedProperties.current)
      if (!closestProperty) return acc

      const currentValue = acc[closestProperty]
      if (currentValue && currentValue.length < inputProperty.length) return acc

      return { ...acc, [closestProperty]: inputProperty }
    }, {} as Record<string, any>)
    if (isEqual(autoMapped, leadMapping)) return

    setLeadMapping(autoMapped)
  }, [cachedInputMap.current, cachedProperties.current])

  const alertShown = useRef(false)
  useEffect(() => {
    if (!repeatedInputs.length || alertShown.current) return

    alertShown.current = true
    alert('Please map each field to a unique value')
  }, [repeatedInputs])

  function updateLeadMapping(property: string, input: string) {
    const updated = { ...leadMapping, [property]: input }
    setLeadMapping(updated)
  }

  return (
    <Flex ref={_ref} direction="column" gap={3} {...rest}>
      <SimpleGrid w="100%" columns={3} gap={3}>
        <Text textStyle="bodySmall">Momentify Fields</Text>
        <Text textStyle="bodySmall">{inputFieldLabel}</Text>
        <Text textStyle="bodySmall">{valueFieldLabel}</Text>
      </SimpleGrid>

      {visibleProperties.map(field => {
        const value = leadMapping[field]

        return (
          <FieldMapper
            error={repeatedInputs.some(fields => value && fields.includes(value))}
            key={field}
            onChange={value => updateLeadMapping(field, value)}
            referenceField={field}
            targetFieldsAndValues={inputMap}
            value={value}
          />
        )
      })}

      {hiddenFields.length > 0 && (
        <SimpleGrid w="100%" columns={3} gap={3}>
          <Menu
            hideCheckbox
            placeholder="Other Properties"
            variant="outline"
            borderRadius="sm"
            options={hiddenFields.map(field => ({ value: field }))}
            onChange={value => updateLeadMapping(value, '')}
          />
        </SimpleGrid>
      )}

      {!hideCustom && customProperties.length > 0 && (
        <Collapsible
          label={`Custom Fields ${
            customProperties.length > 0 ? ` (${customProperties.length})` : ''
          }`}
        >
          <Flex direction="column" gap={3}>
            <Text textStyle="bodyXSmall" opacity={0.6} fontWeight="normal">
              Any additional fields brought in from data that does not match the mapping above will
              appear here.
            </Text>
            <Flex w="100%" gap={3}>
              <Text textStyle="bodySmall" w="60%">
                Field Name
              </Text>
              <Text textStyle="bodySmall" w="40%">
                Field Value
              </Text>
            </Flex>
            {customProperties.map(property => (
              <Flex key={property} w="100%" gap={3}>
                <Text w="60%" {...textStyling}>
                  {property}
                </Text>
                <Text {...textStyling} w="40%">
                  {inputMap?.[property]}
                </Text>
              </Flex>
            ))}
          </Flex>
        </Collapsible>
      )}
    </Flex>
  )
}

const textStyling = {
  bg: 'rgba(23, 23, 23, 0.10)',
  borderRadius: '10px',
  minW: '100px',
  h: '40px',
  py: '10px',
  px: '16px',
  textStyle: 'bodyMedium',
  fontWeight: '500',
  truncate: true,
}

function getNormalizedKey(key: string) {
  return key.toLowerCase().replace(/[\s_-]+/g, '')
}

function getClosestProperty(input: string, properties: string[]) {
  const normalizedKey = getNormalizedKey(input)
  let mostSimilarMatch = ''

  for (const property of properties) {
    const propertyLower = getNormalizedKey(property)
    if (propertyLower === normalizedKey) return property
    if (normalizedKey.includes(propertyLower)) mostSimilarMatch = property
  }

  return mostSimilarMatch
}
